import { cn } from "@lib/cn"
import { PortableText } from "@portabletext/react"
import { FC } from "react"
import { SanityBlock, SanityKeyed } from "sanity-codegen"
import { Link } from "../../__generated__/sanity"
import { Heading } from "../ui/typograhpy/Heading"
import { Text } from "../ui/typograhpy/Text"

type Variants = "special" | "regular" | "large" | "getInTouch" | "small"

export type RichTextBlockProps = {
  content?: Array<SanityKeyed<SanityBlock | Link>>
  variant?: Variants
  className?: string
}

export const RichTextBlock: FC<RichTextBlockProps> = ({
  className,
  content,
  variant = "regular",
}) => {
  if (!content) return null

  const textVariant = ["getInTouch", "small"].includes(variant)
    ? "small"
    : variant === "regular"
      ? "regular"
      : "large"

  return (
    <div className={cn("transition-all duration-200 ease-in-out", className)}>
      <PortableText
        value={content}
        components={{
          marks: {
            strong: ({ children }) => (
              <strong
                className={cn(
                  variant === "special"
                    ? "bg-default box-decoration-clone bg-clip-text font-normal text-transparent"
                    : "text-transparant bg-none font-bold",
                )}
              >
                {children}
              </strong>
            ),
            em: ({ children }) => (
              <em
                className={cn(
                  "bg-default box-decoration-clone bg-clip-text not-italic text-transparent",
                )}
              >
                {children}
              </em>
            ),

            link: ({ value, children }) => {
              return (
                <a
                  href={value?.href}
                  className={cn(
                    "text-current-color hover:underline hover:opacity-80",
                  )}
                >
                  {children}
                </a>
              )
            },
          },
          block: {
            h1: ({ children }) => (
              <Heading variant="h2" as="h1" id={idFromTitle(children)}>
                {children}
              </Heading>
            ),
            h2: ({ children }) => (
              <Heading
                variant="h3"
                as="h2"
                className={cn("mb-2")}
                id={idFromTitle(children)}
              >
                {children}
              </Heading>
            ),
            h3: ({ children }) => (
              <Heading variant="h4" as="h3" className={cn("mb-1 sm:mb-2")}>
                {children}
              </Heading>
            ),
            h4: ({ children }) => (
              <Heading
                variant="h5"
                as="h4"
                className={cn("mb-1")}
                id={idFromTitle(children)}
              >
                {children}
              </Heading>
            ),
            h5: ({ children }) => (
              <Heading variant="h5" as="h5" id={idFromTitle(children)}>
                {children}
              </Heading>
            ),
            normal: ({ children }) => (
              <Text variant={textVariant} as="p" className={cn("mb-2 sm:mb-4")}>
                {children}
              </Text>
            ),
          },
          list: {
            bullet: ({ children }) => (
              <ul className={cn("mb-6 sm:mb-10")}>{children}</ul>
            ),
          },
          listItem: ({ children }) => (
            <Text
              as="li"
              variant={textVariant}
              className={cn("mb-2 ml-8 list-outside list-[initial] last:mb-0")}
            >
              {children}
            </Text>
          ),
        }}
      />
    </div>
  )
}

function idFromTitle(title: any) {
  if (Array.isArray(title)) {
    return title
      .join(" ")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
  }
  if (typeof title === "string") {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, "-")
  }
  return undefined
}
